import React, { FunctionComponent, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Checkbox, TextArea, Icon, Modal, Badge, Loader } from "@scuf/common";
import { ERROR_MSG_FOR_USER_RESPONSE, HAS_SCRIPT, ACTION_UPLOAD_TYPE, CHECKLIST_UPLOAD_TYPE, FILE_UPLOAD_WARNING,ACTION_ALREADY_COMPLETED_UPLOAD_TEXT, PROPOSED_ACTION_TRACKING_DISPLAY_CATEGORIES } from "../../../constants/constants";
import { IUserResponseModel } from "../../../models/IOpportunitiesAPIModel";
import { getReasons, getUploadedFiles, getTrainingOptions, getProposedActionTracking } from "../../../services/Opportunities";
import { FileUpload } from "../../UploadFileModal/FileUpload";
import { USER_ROLE } from '../../../constants/constants';
import { ActionTracking } from '../../ActionTracking/ActionTracking';
import { getIsActionAlreadyCompleted } from "src/slices/actionTrackingSlice";
import {getCurrentRole} from 'src/slices/onBoardingSlice';
import DOMPurify from 'dompurify';
import "./AcceptPopup.scss";
import "../NewCardDesign/CommonStyle.scss";
import { getMessage } from "src/message/utils";
import { AttachmentDownload } from "./AttachmentDownload";


interface IAcceptPopup {
  onClose: Function;
  insightId: any;
  kpiId: number;
  title: string;
  textAreaTitle: string;
  opportunityId: any;
  sendUserResponse: Function;
  activityDetails: any;
  closeAction?: Function;
  role_name?: string;
  anomalyId?: string;
  orguserId?: string;
  errorMessage: string;
  insightSummaryDisplayCategory?: string;
  modelId?: number;
}

const TRAINING_REQUIRED_ACTION_TYPE = 'acceptanddropdown';
const ACTION_OWNER_REQUIRED_ACTION_TYPE = 'acceptwithproposedactionowner';
export const AcceptPopup: FunctionComponent<IAcceptPopup> = (props) => {
  const [enableRetryErr, setEnableRetryErr] = useState(false); // flag to enable submit button
  const [acceptActionInProgress, setacceptActionInProgress] = useState(false);
  const [btnText, setBtnText] = useState("Submit");
  const [remark, setRemark] = useState("");
  const [showLableBardge, setShowLableBardge] = useState(false);
  const [submitBtnDisable, setSubmitBtnDisable] = useState(true);
  const [acceptReason, setAcceptReason] = useState([] as any);
  const [selctedPolicy, setselctedPolicy] = useState([] as any); // flag to enable submit button
  const [selctedDesStatus, setselctedDesStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [reasonLoading, setReasonLoading] = useState(true);
  const [reasonError, setReasonError] = useState(false);
  const [showRemarkError, setShowRemarkError] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [emailItemData, setEmailItemData] = useState([] as any);
  const [shareToAction, setShareToAction] = useState([] as any);
  const [actionComplete, setActionCompete] = useState(false);
  const currentRole = useSelector(getCurrentRole);
  const warningText = getMessage('app.notes.footer.warning');
  const [fileUploadedData, setFileUploadedData] = useState([] as any);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [trainingOptions, setTrainingOptions] = useState([] as any);
  const [proposedActionTracking, setProposedActionTracking] = useState([] as any);
  const [proposedDataReady, setProposedDataReady] = useState(false);
  const isProposedTrackingFlow = PROPOSED_ACTION_TRACKING_DISPLAY_CATEGORIES.includes(props.insightSummaryDisplayCategory || '');
  const isOrgOwnerUI5 = props.role_name === USER_ROLE.ORG_USER &&
    props.insightSummaryDisplayCategory === 'UI 5';
  const [hasRequiredEvidence, setHasRequiredEvidence] = useState(false);

  interface FileDetails {
    file_details: fileData,
  };
  
  type fileData = {
    file_name: string,
    stored_file_name: string
  };

  const isActionAlreadyCompleted = useSelector(getIsActionAlreadyCompleted);
  const reviewerFiles = fileUploadedData?.activity?.filter((file: { type: string; file_details?: { file_name?: string } }) =>
    file.type === CHECKLIST_UPLOAD_TYPE &&
    typeof file.file_details?.file_name === "string" &&
    file.file_details.file_name.trim().length > 0
  ) || [];

  useEffect(() => {
    if (!isOrgOwnerUI5) {
      return;
    }
    let active = true;
    setHasRequiredEvidence(false);
    getUploadedFiles(props.opportunityId)
      .then((response: any) => {
        const hasFile = response.activity.some((activity: any) =>
          activity.type === ACTION_UPLOAD_TYPE &&
          typeof activity.file_details?.file_name === 'string' &&
          activity.file_details.file_name.trim().length > 0
        );
        if (active) {
          setHasRequiredEvidence(hasFile);
        }
      })
      .catch(() => {
        if (active) {
          setHasRequiredEvidence(false);
        }
      });
    return () => { active = false; };
  }, [isOrgOwnerUI5, props.opportunityId]);

  useEffect(() => {
    if (isProposedTrackingFlow) {
      Promise.all([
        getTrainingOptions()
          .then((response: any) => {
            const options = (response || []).map((item: any) => ({
              value: item.training_name,
              text: item.training_name
            }));
            setTrainingOptions(options);
          })
          .catch(() => {
            setTrainingOptions([]);
          }),
        getProposedActionTracking(props.opportunityId)
          .then((response: any) => {
            setProposedActionTracking(response || []);
          })
          .catch(() => {
            setProposedActionTracking([]);
          })
      ]).finally(() => {
        setProposedDataReady(true);
      });
    }
  }, []);

  // flag to enable submit button
  useEffect(() => {
    setReasonLoading(true);
    setReasonError(false);
    Promise.all([
      getReasons(props.opportunityId, "accept"),
      getReasons(props.opportunityId, TRAINING_REQUIRED_ACTION_TYPE),
      getReasons(props.opportunityId, ACTION_OWNER_REQUIRED_ACTION_TYPE)
    ])
      .then(([acceptReasons, trainingReasons, actionOwnerReasons]) => {
        setReasonLoading(false);
        setAcceptReason([...acceptReasons, ...trainingReasons, ...actionOwnerReasons]);
      })
      .catch(() => {
        setReasonLoading(false);
        setReasonError(true);
      });
  }, [props.opportunityId]);

  useEffect(() => {
    getUploadedFiles(props.opportunityId)
      .then((response) => {
        setReasonLoading(false);
        setFileUploadedData(response);
      })
      .catch(() => {
        setReasonLoading(false);
        setReasonError(true);
      });
      
  }, [props.opportunityId, isUploaded]);
  useEffect(() => {
    if (props.errorMessage.length > 0) {
      setBtnText("Retry");
    }
  }, [props.errorMessage]);
  useEffect(() => {
    if (
      props.activityDetails != undefined &&
      props.activityDetails.length > 0
    ) {
      if (props.activityDetails[0].action_details.user_response == "accept") {
        const reasons = props.activityDetails[0].action_details.reason;
        let reasonArray = reasons.split(",");
        setselctedPolicy(reasonArray);
        const reviewerRemark = props.activityDetails[0].action_details.remarks;
        setRemark(reviewerRemark);
        setselctedPolicy(reasonArray);
        if (acceptReason.length > 0) {
          acceptReason.forEach((element: any) => {
            element.checked = false;
          });
          reasonArray.map((reasonValue: string) => {
            let objIndex = acceptReason.findIndex(
              (obj: any) => obj.title == reasonValue
            );
            if (objIndex >= 0) {
              acceptReason[objIndex].checked = true;
            }
          });
          let checkedArray = acceptReason.filter(
            (data: any) => data.checked == true
          );
          setAcceptReason(acceptReason);
          let requireRemarkArray: any = [];
          for (let i = 0; i < checkedArray.length; i++) {
            requireRemarkArray.push(checkedArray[i].requires_remarks);
          }
          setselctedDesStatus(requireRemarkArray);
          setRefresh(!refresh);
          setReasonLoading(false);
          compareActions(remark);
        }
      }
    }

  }, [acceptReason.length]);

  useEffect(() => {
    if (shareToAction.length > 0) {
      let actionIncomplete = shareToAction.filter((item: any) => {
        return (
          item.isValid === false
        )
      })
      if (actionIncomplete.length === 0) {
        setActionCompete(true);
      } else {
        setActionCompete(false);
      }

    }
  }, [actionComplete, shareToAction]);

  const setRemarkHandleChange = (e: string) => {
    setRemark(e.trimStart());
    compareActions(e);
    setRefresh(!refresh);
    if (e.trimStart() === "") {
      let result = selctedDesStatus.filter((item) => item === true);
      if (result.length == 0) {
        setSubmitBtnDisable(false);
        setRefresh(!refresh);
      }
      if (result.length > 0) {
        setSubmitBtnDisable(true);
        setRefresh(!refresh);
      }
    }
  };
  const compareActions = (newremark: string): boolean => {
    if (
      props.activityDetails != undefined &&
      props.activityDetails.length > 0
    ) {
      if (props.activityDetails[0].action_details.user_response == "accept") {
        const reasons = props.activityDetails[0].action_details.reason;
        let reasonArray = reasons.split(",");
        const reviewerRemark = props.activityDetails[0].action_details.remarks;
        if (reasonArray.length == selctedPolicy.length) {
          const data = reasonArray.filter((res: string) =>
            selctedPolicy.includes(res)
          );
          if (
            data.length == selctedPolicy.length &&
            newremark === reviewerRemark
          ) {
            setBtnText("Assign Action");
            setSubmitBtnDisable(false);
            return false;
          }
        }
      }
    }
    setBtnText("Submit");
    setSubmitBtnDisable(false);
    return true;
  };
  const handleCheckbox = (
    id: number,
    check: boolean,
    name: string,
    Des: boolean
  ) => {
    if (check === true) {
      selctedPolicy.splice(selctedPolicy.indexOf(name), 1); // @ts-ignore
      selctedDesStatus.splice(selctedDesStatus.indexOf(Des), 1);
    } else {
      selctedPolicy.push(name); // @ts-ignore
      selctedDesStatus.push(Des);
    }
    let showBardge = selctedDesStatus.filter((item) => item === true);
    compareActions(remark);
    if (showBardge.length >= 1) {
      setShowLableBardge(true);
      setSubmitBtnDisable(true);
    } else if (selctedDesStatus.length === 0) {
      setSubmitBtnDisable(true);
      setShowLableBardge(false);
    } else {
      setShowLableBardge(false);
      setSubmitBtnDisable(false);
    }
    let newState = acceptReason.map((item: any) =>
      item.reason_id === id ? { ...item, checked: check ? false : true } : item
    );
    setAcceptReason(newState);
    let shareToActionValue = [...shareToAction]
    let newShareToActionValue = [];
    if (check === true) {
      newShareToActionValue = shareToActionValue.filter((item: any) => {
        return item.reason_id !== id;
      })
      setShareToAction(newShareToActionValue);
    }
  };

  const onAcceptClicked = () => {
    if (isOrgOwnerUI5 && !hasRequiredEvidence) {
      return;
    }
    const feedback = selctedPolicy.toString();
    setSubmitBtnDisable(true);
    setEnableRetryErr(false);
    setBtnText("Submitting...");
    let inValidCharacter = /[<>()&#]/;
    let data: IUserResponseModel = {
      current_role: currentRole,
      user_response: "accept",
      kpi_id: 1,
      remarks: remark,
      reason: feedback,
    };
    if (props.role_name === "insight.org_user") {
      data = {
        current_role: currentRole,
        user_response: "accept",
        kpi_id: 1,
        remarks: remark,
        reason: feedback,
        accept_as_is: btnText === "Assign Action" ? true : false,
      };

    } else if (props.role_name === "insight.owner") {
      data = {
        current_role: currentRole,
        user_response: "accept",
        kpi_id: 1,
        remarks: remark,
        reason: feedback,
        orguser_id: props.orguserId,
        anomaly_id: props.anomalyId,
      };
    }
    if (!inValidCharacter.test(remark)) {
      if (props.role_name === USER_ROLE.REVIEWER) {
        props.sendUserResponse(data, emailItemData);

      }

      if (props.role_name === USER_ROLE.ORG_USER) {
        let errorScript = false;
        let shareToActionRemoveIsValidL1 = shareToAction.map(({ isValid, ...rest }: any) => rest);
        var shareToActionRemoveIsValidL2: any = [];
        shareToActionRemoveIsValidL1.forEach((item: any) => {
          let newData: any = [];
          item.data.forEach((subItem: any) => {
            delete subItem.isValid;
            delete subItem.action_owner_email;
            delete subItem.createdOn;
            let modifyRecord = subItem;
            errorScript = inValidCharacter.test(subItem.remarks)
            if (errorScript) {
              setShowRemarkError(true);
              setBtnText(btnText);
            }
            newData.push(modifyRecord);
          })
          shareToActionRemoveIsValidL2.push({ reason_id: item.reason_id, reason: item.reason, data: newData })
        })
        data["share_to"] = shareToActionRemoveIsValidL2;
       // data["current_role"] =currentRole;
        if (!errorScript) {
          props.sendUserResponse(data, shareToActionRemoveIsValidL2);
        }

      }
    } else {
      setShowRemarkError(true);
      setBtnText("Submit");
    }
  };
  const onAddedActionEmail = (shareToElementValue: any, isValidActionReasonStatus: boolean) => {
    // Functional update avoids a lost-update race when multiple reasons' ActionTracking mount/update in the same render tick
    setShareToAction((prevShareToAction: any) => {
      const existingIndex = prevShareToAction.findIndex((item: any) => item.reason_id === shareToElementValue.reason_id);
      if (existingIndex >= 0) {
        const next = [...prevShareToAction];
        next[existingIndex] = { ...shareToElementValue, isValid: isValidActionReasonStatus };
        return next;
      }
      return [...prevShareToAction, { ...shareToElementValue, isValid: isValidActionReasonStatus }];
    });
  }

  const handlesubmitDisabled = () => {
    if (props.role_name === USER_ROLE.ORG_USER) {
      return (isUploaded) || (!actionComplete) || selctedPolicy.length == 0 ||
        !disclaimerAccepted || (isOrgOwnerUI5 && !hasRequiredEvidence) ? true : false
    }
    if (props.role_name === USER_ROLE.REVIEWER) {
      return (submitBtnDisable && remark.trimStart() === "" || isUploaded) || selctedPolicy.length == 0 ? true : false
    }
    return submitBtnDisable;
  }


  return (
    <>
      {/* <Modal.Header className="accept-header"> {props.title} </Modal.Header> */}
      <Modal.Content>
        <div >
          <div style={{ position: "relative" }} data-testid="getInvoiceCard">
            {reasonLoading && (
              <Loader
                loading={reasonLoading}
                text="Fetching reasons..."
                minHeight="28vh"
                max-height="70vh"
                overlayColor="transparent"
              />
            )}

            {reasonError && (
              <div className="action--error-text">
                {ERROR_MSG_FOR_USER_RESPONSE}
              </div>
            )}
            {acceptReason.map((item: any, index: any) => (
              <div>
                {/** Training controls are selected by the reason action type. */}
                {(() => {
                  const reasonTitle = (item.title || "").toString().trim().toLowerCase();
                  const isTrainingRequiredReason =
                    item.action_type === TRAINING_REQUIRED_ACTION_TYPE &&
                    reasonTitle.includes("training required");

                  return (
                    <>
                <Checkbox
                  className="checkbox-fi"
                  data-testid={`cb-reason1${index}`}
                  label={
                    item.desc !== null
                      ? `${item.title} ${item.desc}`
                      : `${item.title}`
                  }
                  checked={item.checked}
                  onChange={() => {
                    handleCheckbox(
                      item.reason_id,
                      item.checked,
                      item.title,
                      item.requires_remarks
                    );
                  }}
                />
              
                {
                  item.checked && props.role_name === USER_ROLE.ORG_USER && <ActionTracking
                    key={item.reason_id}
                    reasonId={item.reason_id}
                    reasonTitle={item.title}
                    actionType={item.action_type}
                    isChecked={item.checked}
                    isCommentRequired={item.requires_remarks}
                    onAddedActionEmail={onAddedActionEmail}
                    trainingOptions={isTrainingRequiredReason ? trainingOptions : []}
                    proposedActionTracking={isProposedTrackingFlow ? proposedActionTracking : []}
                    isProposedTrackingFlow={isProposedTrackingFlow}
                    proposedDataReady={proposedDataReady}
                  />
                }
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
        {<>
          <label className="label">
            Remarks{" "}
            {showLableBardge && remark === "" ? (
              <Badge empty={true} color="red" />
            ) : (
              ""
            )}
          </label>

          <TextArea
            onChange={setRemarkHandleChange}
            className="textarea-wrapper textarea-fi accept-gap"
            fluid={true}
            rows={2}
            placeholder={props.textAreaTitle}
            value={remark.trimStart()}
          />
        </>
        }

        {props.role_name === USER_ROLE.ORG_USER && <>
          <label className="label">
            Reviewer's Remarks:{" "} <i> {remark}</i>
            {showLableBardge && remark === "" ? (
              <Badge empty={true} color="red" />
            ) : (
              ""
            )}
          </label>
          {reviewerFiles.length > 0 && (
            <section aria-label="Reviewer uploaded files">
              <div className="accept-popup__uploadfiles accept-popup__uploadfiles--reviewer">Files Uploaded by Reviewer</div>
              <div className="file-value-wrapper old-files-wrapper">
                {reviewerFiles.map((file: FileDetails, index: number) => (
                  <span className="file-value-wrapper-content" key={`${index}-${file.file_details.file_name}`}>
                    <Icon name="document-report" root="common" size="small" className="file-value-icon" />
                    <div className="file-value-wrapper-content-data">{file.file_details.file_name}</div>
                    <AttachmentDownload opportunityId={props.opportunityId} fileName={file.file_details.file_name} storedFileName={file.file_details.stored_file_name} />
                  </span>
                ))}
              </div>
            </section>
          )}
        </>
        }
        <div className="accept-popup__uploadfiles">
          Upload Files
          {isOrgOwnerUI5 && !hasRequiredEvidence && (
            <span
              className="upload-required-indicator"
              role="img"
              aria-label="Supporting file required"
            >
              <Badge empty={true} color="red" />
            </span>
          )}
        </div>
        {isActionAlreadyCompleted && <div className="accept-popup__uploadfilesnote">{ACTION_ALREADY_COMPLETED_UPLOAD_TEXT}</div>}
        <div className='email-error mt-3' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(warningText,{
                ALLOWED_ATTR:['href', 'target']
            }) }}></div>
        <div className="upload-wrapper_reasons">
          <FileUpload
            className="document-upload" // @ts-ignore
            anomaly_id={props.opportunityObjectName}
            opportunityId={props.opportunityId}
            type={ACTION_UPLOAD_TYPE}
            updateUploadedStatus={(uploaded: boolean) => setIsUploaded(uploaded)}
            onUploadSuccess={isOrgOwnerUI5 ? () => setHasRequiredEvidence(true) : undefined}
          />
        </div>
        {isUploaded && <div className="accept-popup__remarkerr">{FILE_UPLOAD_WARNING}</div>}
        { fileUploadedData?.activity?.length > 0 && <div className="file-value-wrapper old-files-wrapper">
                {fileUploadedData?.activity.filter((fileData:any) => fileData.type === "action-upload").map((file: FileDetails, index: any) =>(
                    file && <span 
                                className="file-value-wrapper-content"
                                key={`${index}-${file?.file_details?.file_name}`}>
                                    <Icon 
                                        name="document-report" 
                                        root="common"
                                        size="small"
                                        className='file-value-icon'/>
                                    <div className='file-value-wrapper-content-data'> {` ${file?.file_details?.file_name}`}</div>
                                    <AttachmentDownload opportunityId={props.opportunityId} fileName={file.file_details.file_name} storedFileName={file.file_details.stored_file_name} />
                            </span>))}
            </div>}
         {props.role_name === "insight.org_user" && (
          <div className="disclaimer-section">
            <Checkbox
              className="checkbox-fi"
              data-testid="disclaimer-checkbox"
              label="CAMS is not yet approved for use in France, Germany, Italy, Austria. Actions can be assigned only to action owners outside of these countries."
              checked={disclaimerAccepted}
              onChange={() => setDisclaimerAccepted(!disclaimerAccepted)}
            />
          </div>
        )}

      </Modal.Content>

      <Modal.Footer className="accept-popup__footer pop-footer">
        {showRemarkError && (
          <div className="accept-popup__footer--err err-msg">{HAS_SCRIPT}</div>
        )}
        {enableRetryErr && (
          <div className="accept-popup__footer--err err-msg">
            {ERROR_MSG_FOR_USER_RESPONSE}
          </div>
        )}
        {/* {(props.errorMessage.length>0)&&
              <div className="accept-popup__footer--err">
                {props.errorMessage}
              </div>
            } */}
        <Button
          className="button--secondary button-space"
          data-testid="cancel"
          size="small"
          type="secondary"
          disabled={acceptActionInProgress}
          onClick={() => {
            props.onClose();
          }}
          content="Cancel"
        />
        <Button
          className={`button-space button--primary ${acceptActionInProgress
              ? "accept-popup__footer--readonly btn-loading"
              : ""
            }`}
          icon={
            enableRetryErr ? (
              <Icon
                root="building"
                name="refresh"
                exactSize={22}
                className="accept-popup__footer--retry"
              />
            ) : (
              ""
            )
          }
          iconPosition="right"
          size="small"
          disabled={handlesubmitDisabled()}
          type="primary"
          data-testid="submit"
          onClick={onAcceptClicked}
          content={btnText}
        />
        <div className="action--error-text error-submit">{props.errorMessage}</div>
      </Modal.Footer>
    </>
  );
};
